import { useId, useState } from 'react'
import type { ReactNode } from 'react'

import { ApiError } from '@/api/client'
import type { ListInput } from '@/api/endpoints'
import type { UserList } from '@/api/types'
import { Button } from '@/components/ui/Button'
import { Toggle } from '@/features/settings'
import { useT } from '@/i18n'
import { cn } from '@/lib/cn'
import { countChars } from '@/lib/text'

const NAME_MAX = 25
const DESCRIPTION_MAX = 100

interface Props {
  /** 改的时候给原来的列表；建的时候不给 */
  initial?: UserList
  submitLabel: string
  pending: boolean
  error: unknown
  onSubmit: (input: ListInput) => void
}

/** 列表的名字、描述、私密。建列表对话框与「添加到列表」里的新建共用。 */
export function ListForm({ initial, submitLabel, pending, error, onSubmit }: Props) {
  const t = useT()
  const [name, setName] = useState(initial?.name ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [isPrivate, setIsPrivate] = useState(initial?.is_private ?? false)

  const nameId = useId()
  const descriptionId = useId()
  const apiError = error instanceof ApiError ? error : null
  const nameTooLong = countChars(name.trim()) > NAME_MAX
  const canSubmit = name.trim().length > 0 && !nameTooLong && !pending

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault()
        if (!canSubmit) return
        onSubmit({
          name: name.trim(),
          description: description.trim() || null,
          is_private: isPrivate,
        })
      }}
    >
      <Field
        id={nameId}
        label={t('lists.name')}
        count={countChars(name.trim())}
        max={NAME_MAX}
        error={apiError?.fieldError('name')}
      >
        <input
          id={nameId}
          value={name}
          onChange={(event) => setName(event.target.value)}
          maxLength={NAME_MAX * 2}
          autoFocus
          className={fieldClass(Boolean(apiError?.fieldError('name')) || nameTooLong)}
        />
      </Field>
      <Field
        id={descriptionId}
        label={t('lists.description')}
        count={countChars(description.trim())}
        max={DESCRIPTION_MAX}
        error={apiError?.fieldError('description')}
      >
        <textarea
          id={descriptionId}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          maxLength={DESCRIPTION_MAX * 2}
          rows={2}
          className={cn(fieldClass(Boolean(apiError?.fieldError('description'))), 'resize-none')}
        />
      </Field>
      <Toggle
        label={t('lists.private')}
        description={t('lists.privateHint')}
        checked={isPrivate}
        onChange={setIsPrivate}
      />
      {apiError && !apiError.details && <p className="text-sm text-danger">{apiError.message}</p>}
      <Button type="submit" fullWidth size="lg" loading={pending} disabled={!canSubmit}>
        {submitLabel}
      </Button>
    </form>
  )
}

function fieldClass(hasError: boolean) {
  return cn(
    'w-full rounded-md border bg-surface px-3 py-2 text-[15px] outline-none focus:border-brand-ink',
    hasError ? 'border-danger' : 'border-line-strong',
  )
}

function Field({
  id,
  label,
  count,
  max,
  error,
  children,
}: {
  id: string
  label: string
  count: number
  max: number
  error?: string
  children: ReactNode
}) {
  return (
    <div>
      <div className="mb-1 flex justify-between text-sm text-ink-muted">
        <label htmlFor={id}>{label}</label>
        <span aria-hidden className={cn('font-mono', count > max && 'text-danger')}>
          {count}/{max}
        </span>
      </div>
      {children}
      {error && <p className="mt-1 text-sm text-danger">{error}</p>}
    </div>
  )
}
